'use client';

import * as React from 'react';
import type { CSSProperties, MouseEvent } from 'react';

export type IdTagSize = 'xs' | 'sm';

export interface IdTagProps {
  id?: string | number | null;
  size?: IdTagSize;
}

type CopyStatus = 'idle' | 'copied' | 'error';

/* Renders as a <button> so keyboard users can tab to it and press
   Enter / Space to copy — the "click to copy" UX works without a
   pointer. type="button" prevents accidental form submission when
   embedded in forms. */
export function IdTag({ id, size = 'sm' }: IdTagProps) {
  const [status, setStatus] = React.useState<CopyStatus>('idle');
  const resetTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  /* mountedRef gates the clipboard promise resolutions so they
     don't setState on an unmounted component. Common case: an
     IdTag inside a toast that dismisses on copy — the state
     update from the resolved writeText would otherwise race the
     unmount and warn / no-op on older React. */
  const mountedRef = React.useRef(true);

  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    };
  }, []);

  if (id == null || id === '') return null;

  const scheduleReset = () => {
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    resetTimerRef.current = setTimeout(() => {
      if (mountedRef.current) setStatus('idle');
    }, 1200);
  };
  const safeSetStatus = (s: CopyStatus) => {
    if (mountedRef.current) setStatus(s);
  };

  const copy = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    /* navigator.clipboard.writeText is async — the permission /
       user-activation check resolves on a microtask. Hang the UI
       feedback on the returned promise's resolution so we don't
       claim a copy happened when the write later rejected (insecure
       origin, iframe sandbox, permission denied). The three-state
       status model (idle / copied / error) gives consumers a
       data-status hook to style failure distinct from success. */
    const p = navigator.clipboard?.writeText(String(id));
    if (!p) {
      /* No clipboard API at all — surface the error state. */
      safeSetStatus('error');
      scheduleReset();
      return;
    }
    p.then(() => {
      safeSetStatus('copied');
      scheduleReset();
    }).catch(() => {
      safeSetStatus('error');
      scheduleReset();
    });
  };

  const style: CSSProperties | undefined =
    size === 'xs' ? { fontSize: 'var(--type-nano)' } : undefined;

  const label = status === 'copied' ? '✓ copied' : status === 'error' ? '⚠ failed' : `#${id}`;
  const ariaLabel =
    status === 'copied' ? 'Copied' : status === 'error' ? 'Copy failed' : `Copy ID ${id}`;
  const titleText =
    status === 'copied' ? 'Copied!' : status === 'error' ? 'Copy failed' : `Click to copy "${id}"`;

  return (
    <button
      type="button"
      className="j-id-tag"
      /* Preserves the 3.0.0 data-copied hook for existing CSS.
         Additive data-status lets consumers style the error path
         distinctly without reading the stringly-typed data-copied. */
      data-copied={status === 'copied' ? 'true' : undefined}
      data-status={status}
      onClick={copy}
      aria-label={ariaLabel}
      title={titleText}
      style={style}
    >
      {label}
    </button>
  );
}
